import { useCallback, useMemo } from 'react';
import type { RackEquipmentItem } from '../components/canvas/rackTypes';
import {
  checkEquipmentConflict,
  calculateFreeUWithFaces,
} from '../utils/halfDepth.logic';
import {
  calculateTotalWeight,
  calculateTotalPower,
  checkReservationWarning,
  calculateRackMetrics,
} from '../utils/rackMetrics.logic';

interface RackConfig {
  u_slots?: number;
  max_weight_kg?: number;
  max_power_w?: number;
  [key: string]: any;
}

interface UseRackValidationParams {
  currentRack: RackConfig | null;
  equipmentList: RackEquipmentItem[];
  editingItem: RackEquipmentItem | null;
  slots: number;
  reservations?: any[];
}

export function useRackValidation({ currentRack, equipmentList, editingItem, slots, reservations = [] }: UseRackValidationParams) {

  /** Pre-flight validate: checks weight, power, slot conflicts, and reservation warnings. */
  const handleValidate = useCallback(async (data: any) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!currentRack) return { errors, warnings };

    const maxWeight = currentRack.max_weight_kg || 1000;
    const maxPower  = currentRack.max_power_w   || 5000;

    const filteredEq = equipmentList.filter(eq => eq.id !== editingItem?.id);
    const curWeight = calculateTotalWeight(filteredEq);
    const curPower  = calculateTotalPower(filteredEq);

    const newWeight = Number(data.weight_kg) || 0;
    const newPower  = Number(data.power_w)   || 0;

    if (curWeight + newWeight > maxWeight) {
      errors.push(`Превышен лимит веса: ${curWeight + newWeight}кг / ${maxWeight}кг`);
    }

    if (curPower + newPower > maxPower) {
      warnings.push(`Высокое энергопотребление: ${curPower + newPower}Вт / ${maxPower}Вт`);
    }

    const requestedU       = Number(data.u_position);
    const requestedHeight  = Number(data.u_height) || 1;
    const requestedMounting = data.mounting_position || 'Front';

    if (requestedU < 1 || (requestedU + requestedHeight - 1) > slots) {
      errors.push(`Недопустимая позиция: U${requestedU}..U${requestedU + requestedHeight - 1} выходит за пределы ${slots}U`);
    } else {
      const candidate = {
        name: data.name || 'Новое оборудование',
        u_position: requestedU,
        u_height: requestedHeight,
        mounting_position: requestedMounting,
      };

      // Check physical conflict with existing equipment
      for (const eq of filteredEq) {
        if (eq.is_zero_u) continue;
        const conflictRes = checkEquipmentConflict(candidate, eq);
        if (conflictRes.conflict) {
          errors.push(conflictRes.reason || `Конфликт с прибором '${eq.name}' в слотах U${eq.u_position}`);
          break;
        }
      }

      // Check overlap warning with rack reservations
      const resWarning = checkReservationWarning(candidate, reservations);
      if (resWarning) {
        warnings.push(resWarning);
      }
    }

    return { errors, warnings };
  }, [currentRack, equipmentList, editingItem, slots, reservations]);

  /** Compute face availability and contiguous free U-blocks in the rack (respecting reservations). */
  const faceAvailability = useMemo(() => {
    return calculateFreeUWithFaces(slots || 42, equipmentList, reservations);
  }, [equipmentList, slots, reservations]);

  const freeUBlocks = useMemo(() => {
    // All contiguous blocks available on the front face (including all fully free blocks),
    // ensuring half-depth front slots are not masked by single-unit full-depth blocks.
    return faceAvailability.freeFrontBlocks;
  }, [faceAvailability]);

  /** Unified space metrics: occupied, reserved, available, and utilization percentage. */
  const spaceMetrics = useMemo(() => {
    return calculateRackMetrics(equipmentList, reservations, slots || 42);
  }, [equipmentList, reservations, slots]);

  const usedU        = spaceMetrics.occupiedU;
  const reservedU    = spaceMetrics.reservedU;
  const availableU   = spaceMetrics.availableU;
  const fillPct      = spaceMetrics.occupiedPct;

  /** Weight + power totals for display. */
  const currentWeight = useMemo(() => calculateTotalWeight(equipmentList), [equipmentList]);
  const currentPower  = useMemo(() => calculateTotalPower(equipmentList), [equipmentList]);

  return {
    handleValidate,
    freeUBlocks,
    usedU,
    fillPct,
    reservedU,
    availableU,
    currentWeight,
    currentPower,
    faceAvailability,
    spaceMetrics,
  };
}
